import { createClient } from "@supabase/supabase-js";

export interface PublicSubscriptionPlan {
  id: string;
  name: string;
  description: string | null;
  price: number;
  billing_period: string;
  credits_included: number;
  is_featured: boolean | null;
  stripe_price_id: string | null;
  stripe_price_id_test?: string | null;
  lemonsqueezy_variant_id: string | null;
  status: string;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
}

export interface PublicCreditPackage {
  id: string;
  name: string;
  description: string | null;
  credits_amount: number;
  price: number;
  currency: string;
  is_popular: boolean | null;
  stripe_price_id: string | null;
  stripe_price_id_test?: string | null;
  lemonsqueezy_variant_id: string | null;
  status: string;
}

export interface PublicPricingUnitCosts {
  scriptGeneration: number;
  imageGeneration: number;
  audioGenerationMinimum: number;
  audioGenerationMaximum: number;
  videoComposition: number;
  isFallback: boolean;
}

export interface PublicPricingData {
  plans: PublicSubscriptionPlan[];
  packages: PublicCreditPackage[];
  unitCosts: PublicPricingUnitCosts;
}

export interface VideoCostProfile {
  key: "short" | "explainer" | "long-form" | "podcast";
  durationSeconds: number;
  imageCount: number;
  minimumCredits: number;
  maximumCredits: number;
}

const FALLBACK_UNIT_COSTS: PublicPricingUnitCosts = {
  scriptGeneration: 50,
  imageGeneration: 30,
  audioGenerationMinimum: 1,
  audioGenerationMaximum: 10,
  videoComposition: 5,
  isFallback: true,
};

const VIDEO_COST_PROFILE_INPUTS: Array<
  Pick<VideoCostProfile, "key" | "durationSeconds" | "imageCount">
> = [
  { key: "short", durationSeconds: 60, imageCount: 9 },
  { key: "explainer", durationSeconds: 180, imageCount: 24 },
  { key: "long-form", durationSeconds: 600, imageCount: 76 },
  { key: "podcast", durationSeconds: 540, imageCount: 1 },
];

function parsePositiveCost(value: unknown, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

export function getFallbackPublicPricingData(): PublicPricingData {
  return {
    plans: [],
    packages: [],
    unitCosts: { ...FALLBACK_UNIT_COSTS },
  };
}

const normalizeStripePriceId = <
  T extends {
    stripe_price_id: string | null;
    stripe_price_id_test?: string | null;
  },
>(
  item: T,
  useTestPrice: boolean,
): T => ({
  ...item,
  stripe_price_id: useTestPrice
    ? item.stripe_price_id_test || item.stripe_price_id
    : item.stripe_price_id,
});

export async function getPublicPricingData(): Promise<PublicPricingData> {
  if (
    !process.env.NEXT_PUBLIC_SUPABASE_URL ||
    !process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  ) {
    return getFallbackPublicPricingData();
  }

  const supabase = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    { auth: { persistSession: false } },
  );
  const [
    { data: plans, error: planError },
    { data: packages, error: packageError },
    { data: pricingRows, error: pricingError },
  ] = await Promise.all([
    supabase
      .from("subscription_plans")
      .select("*")
      .eq("status", "active")
      .order("sort_order", { ascending: true }),
    supabase
      .from("credit_packages")
      .select("*")
      .eq("status", "active")
      .order("sort_order", { ascending: true }),
    supabase
      .from("config_param")
      .select("id,config_key,config_value")
      .eq("config_type", "pricing_config")
      .or("status.eq.1,status.is.null")
      .order("id", { ascending: true }),
  ]);

  if (planError || packageError) {
    throw planError || packageError;
  }

  const useTestPrice =
    process.env.NODE_ENV === "development" ||
    process.env.NEXT_PUBLIC_STRIPE_IS_TEST === "true";

  const pricing = new Map<string, unknown>(
    (pricingRows || []).map((row) => [row.config_key, row.config_value]),
  );
  const requiredPricingKeys = [
    "script_generation_fixed_price",
    "image_generation_per_unit_price",
    "audio_generation_azure_per_second",
    "audio_generation_elevenlabs_per_second",
    "video_composition_per_second_price",
  ];
  const isPricingFallback =
    Boolean(pricingError) ||
    requiredPricingKeys.some((key) => !pricing.has(key));
  const unitCosts: PublicPricingUnitCosts = isPricingFallback
    ? { ...FALLBACK_UNIT_COSTS }
    : {
        scriptGeneration: parsePositiveCost(
          pricing.get("script_generation_fixed_price"),
          FALLBACK_UNIT_COSTS.scriptGeneration,
        ),
        imageGeneration: parsePositiveCost(
          pricing.get("image_generation_per_unit_price"),
          FALLBACK_UNIT_COSTS.imageGeneration,
        ),
        audioGenerationMinimum: parsePositiveCost(
          pricing.get("audio_generation_azure_per_second"),
          FALLBACK_UNIT_COSTS.audioGenerationMinimum,
        ),
        audioGenerationMaximum: parsePositiveCost(
          pricing.get("audio_generation_elevenlabs_per_second"),
          FALLBACK_UNIT_COSTS.audioGenerationMaximum,
        ),
        videoComposition: parsePositiveCost(
          pricing.get("video_composition_per_second_price"),
          FALLBACK_UNIT_COSTS.videoComposition,
        ),
        isFallback: false,
      };

  if (unitCosts.audioGenerationMinimum > unitCosts.audioGenerationMaximum) {
    [unitCosts.audioGenerationMinimum, unitCosts.audioGenerationMaximum] = [
      unitCosts.audioGenerationMaximum,
      unitCosts.audioGenerationMinimum,
    ];
  }

  return {
    plans: (plans || []).map((plan) =>
      normalizeStripePriceId(plan, useTestPrice),
    ) as PublicSubscriptionPlan[],
    packages: (packages || []).map((creditPackage) =>
      normalizeStripePriceId(creditPackage, useTestPrice),
    ) as PublicCreditPackage[],
    unitCosts,
  };
}

export function estimateVideoCostProfiles(
  unitCosts: PublicPricingUnitCosts,
): VideoCostProfile[] {
  return VIDEO_COST_PROFILE_INPUTS.map((profile) => {
    const sharedCost =
      unitCosts.scriptGeneration +
      profile.imageCount * unitCosts.imageGeneration +
      profile.durationSeconds * unitCosts.videoComposition;

    return {
      ...profile,
      minimumCredits: Math.max(
        0,
        Math.ceil(
          sharedCost +
            profile.durationSeconds * unitCosts.audioGenerationMinimum,
        ),
      ),
      maximumCredits: Math.max(
        0,
        Math.ceil(
          sharedCost +
            profile.durationSeconds * unitCosts.audioGenerationMaximum,
        ),
      ),
    };
  });
}

export function estimateVideoCostForConfiguration({
  durationSeconds,
  imageCount,
  unitCosts,
}: {
  durationSeconds: number;
  imageCount: number;
  unitCosts: PublicPricingUnitCosts;
}) {
  const duration = Math.max(0, durationSeconds);
  const images = Math.max(0, Math.ceil(imageCount));
  const sharedCost =
    unitCosts.scriptGeneration +
    images * unitCosts.imageGeneration +
    duration * unitCosts.videoComposition;

  return {
    minimumCredits: Math.ceil(
      sharedCost + duration * unitCosts.audioGenerationMinimum,
    ),
    maximumCredits: Math.ceil(
      sharedCost + duration * unitCosts.audioGenerationMaximum,
    ),
  };
}

export function estimatePlanVideoOutput(
  credits: number,
  profile: Pick<VideoCostProfile, "minimumCredits" | "maximumCredits">,
) {
  const availableCredits = Math.max(0, credits);
  return {
    minimum:
      profile.maximumCredits > 0
        ? Math.floor(availableCredits / profile.maximumCredits)
        : 0,
    maximum:
      profile.minimumCredits > 0
        ? Math.floor(availableCredits / profile.minimumCredits)
        : 0,
  };
}

export function estimateIncludedVideoMinutes(credits: number) {
  const conservativeCreditsPerMinute = 750;
  const optimisticCreditsPerMinute = 500;

  return {
    minimum: Math.max(
      0,
      Math.round((credits / conservativeCreditsPerMinute) * 10) / 10,
    ),
    maximum: Math.max(
      0,
      Math.round((credits / optimisticCreditsPerMinute) * 10) / 10,
    ),
  };
}
